import React, { useMemo } from 'react';
import { ShipInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetShipByMmsi } from '@ais-ui/api';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { LoadingSpinner, Separator } from '@ais-ui/ui-core';
import { usePermissions } from '../../Permissions/PermissionsProvider';
import { extractMmsiFromShipInfo } from '../../utils/extractMmsiFromShipInfo';
import { useAuth } from '@ais-ui/auth';
import { useLengthUnits } from '../../Units/useLengthUnits';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

interface ExtendedVesselInfoIdentificationShipTypeProps {
  shipInfo: ShipInfo;
}

const ExtendedVesselInfoIdentificationShipType = ({
  shipInfo,
}: ExtendedVesselInfoIdentificationShipTypeProps) => {
  const permissions = usePermissions();

  const hasShipDbPermissions = permissions.mdmAdvancedVesselInfo;

  const { data, isLoading } = useGetShipByMmsi(
    extractMmsiFromShipInfo(shipInfo),
    hasShipDbPermissions
  );

  const vesselType = useMemo(() => {
    if (!data) {
      return null;
    }

    return data.basic?.vesselType;
  }, [data]);

  if (!hasShipDbPermissions) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">Extended vessel info type</p>
      {isLoading && <LoadingSpinner size={'small'} />}
      {!isLoading && (
        <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
          {isNonEmpty(vesselType) ? vesselType : '-'}
        </p>
      )}
    </div>
  );
};

interface IdentificationProps {
  shipInfo: ShipInfo;
}

const Identification = ({ shipInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <IdentificationItem
        label={messages.shipDetailsBaseNameLabel}
        value={shipInfo.name}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseIMOLabel}
        value={shipInfo.imo}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={shipInfo.mmsi}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCallSignLabel}
        value={shipInfo.callSign}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCountryLabel}
        value={shipInfo.country}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseAISTypeLabel}
        value={shipInfo.aisType}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseSourceTypeLabel}
        value={shipInfo.sourceType}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseShipTypeLabel}
        value={shipInfo.shipType}
      />
      <ExtendedVesselInfoIdentificationShipType shipInfo={shipInfo} />
    </div>
  );
};

interface PhysicalCharacteristicsProps {
  shipInfo: ShipInfo;
}

interface PhysicalCharacteristicsItemProps {
  label: string;
  value: string;
}

const PhysicalCharacteristicsItem = ({
  label,
  value,
}: PhysicalCharacteristicsItemProps) => {
  const { convertLengthString } = useLengthUnits();
  const convertedValue = convertLengthString(value);

  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(convertedValue) ? convertedValue : '-'}
      </p>
    </div>
  );
};

const PhysicalCharacteristics = ({
  shipInfo,
}: PhysicalCharacteristicsProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseWidthLabel}
        value={shipInfo.width}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseLengthLabel}
        value={shipInfo.length}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseDraughtLabel}
        value={shipInfo.draught}
      />
    </div>
  );
};

interface NavigationalInformationProps {
  shipInfo: ShipInfo;
}

interface NavigationalInformationItemProps {
  label: string;
  value: string;
}

const NavigationalInformationItem = ({
  label,
  value,
}: NavigationalInformationItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

const NavigationalInformation = ({
  shipInfo,
}: NavigationalInformationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-4">
      <NavigationalInformationItem
        label={messages.shipDetailsBaseHeadingLabel}
        value={shipInfo.heading}
      />
      <NavigationalInformationItem
        label={messages.shipDetailsBaseROTLabel}
        value={shipInfo.rateOfTurn}
      />
      <NavigationalInformationItem
        label={messages.shipDetailsBaseCOGLabel}
        value={shipInfo.courseOverGround}
      />
      <NavigationalInformationItem
        label={messages.shipDetailsBaseSOGLabel}
        value={shipInfo.speedOverGround}
      />
    </div>
  );
};

interface OperationalStatusProps {
  shipInfo: ShipInfo;
}

interface OperationalStatusItemProps {
  label: string;
  value: string;
}

const OperationalStatusItem = ({
  label,
  value,
}: OperationalStatusItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

const OperationalStatus = ({ shipInfo }: OperationalStatusProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-4">
      <OperationalStatusItem
        label={messages.shipDetailsBaseDestinationLabel}
        value={shipInfo.destination}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseETATitle}
        value={formatDateTimeWithTimeZone(shipInfo.eta)}
      />
      {/* Show port ETA only if it exists, as it's a special port customer field */}
      {shipInfo.portEta && (
        <OperationalStatusItem
          label={messages.shipDetailsBasePortETATitle}
          value={formatDateTimeWithTimeZone(shipInfo.portEta)}
        />
      )}
      <OperationalStatusItem
        label={messages.shipDetailsBaseLatLabel}
        value={shipInfo.latitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLonLabel}
        value={shipInfo.longitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseNavigationStatusLabel}
        value={shipInfo.navigationalStatus}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLatitudeLabel}
        value={shipInfo.lastAisLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLongitudeLabel}
        value={shipInfo.lastAisLongitude}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLastAISReportLabel}
        value={formatDateTimeWithTimeZone(shipInfo.lastAisReport)}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLatitudeLabel}
        value={shipInfo.lastAisSatLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLongitudeLabel}
        value={shipInfo.lastAisSatLongitude}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLastAISSatelliteReportLabel}
        value={formatDateTimeWithTimeZone(shipInfo.lastAisSatReport)}
      />
    </div>
  );
};

interface PortCallItemProps {
  label: string;
  value: string;
}

const PortCallItem = ({ label, value }: PortCallItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : '-'}
      </p>
    </div>
  );
};

type PortCallType = {
  ata: Date | null;
  atd: Date | null;
  port_name: string;
  un_location_code: string;
};

const PortCall = ({ portCall }: { portCall: PortCallType }) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="flex flex-col space-y-2">
      <h4 className="text-sm font-medium">{portCall.port_name}</h4>
      <div className="grid grid-cols-2 gap-4">
        <PortCallItem label="Locode" value={portCall.un_location_code} />
        <PortCallItem
          label="Actual Time of Arrival"
          value={formatDateTimeWithTimeZone(portCall.ata)}
        />
        <PortCallItem
          label="Actual Time of Departure"
          value={formatDateTimeWithTimeZone(portCall.atd)}
        />
      </div>
    </div>
  );
};

const PortCalls = ({ shipInfo }: { shipInfo: ShipInfo }) => {
  const permissions = usePermissions();
  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  const { data, isLoading } = useGetShipByMmsi(
    parseInt(shipInfo.mmsi),
    hasShipDbPermissions
  );

  if (!hasShipDbPermissions) {
    return null;
  }

  return (
    <div className="space-y-8">
      {isLoading && <LoadingSpinner />}
      {data &&
        data.basic &&
        data.basic.portCalls &&
        [...data.basic.portCalls]
          .reverse()
          .map((portCall: PortCallType, index: number) => (
            <PortCall key={index} portCall={portCall} />
          ))}
    </div>
  );
};

interface BaseInformationProps {
  shipInfo: ShipInfo;
}

export const BaseInformation = ({ shipInfo }: BaseInformationProps) => {
  const { messages } = useLocalization();
  const permissions = usePermissions();

  const hasShipDbPermissions =
    permissions.mdmAdvancedVesselInfo ||
    permissions.mdmRegister ||
    permissions.mdmIntegrity;

  useGetShipByMmsi(extractMmsiFromShipInfo(shipInfo), hasShipDbPermissions);

  return (
    <div className="grid gap-6">
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification shipInfo={shipInfo}></Identification>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Physical Characteristics</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <PhysicalCharacteristics shipInfo={shipInfo}></PhysicalCharacteristics>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Navigation Information</h3>
          <Separator className="mt-2"></Separator>
        </div>

        <NavigationalInformation shipInfo={shipInfo}></NavigationalInformation>
      </div>
      <div className="grid gap-4">
        <div>
          <h3 className="text-lg font-medium">Operational Status</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <OperationalStatus shipInfo={shipInfo}></OperationalStatus>
      </div>
      {hasShipDbPermissions && (
        <div className="grid gap-4">
          <div>
            <div className="flex justify-between items-baseline pr-4">
              <h3 className="text-lg font-medium">Port Calls</h3>
              <h3 className="text-md text-muted-foreground">last 3</h3>
            </div>
            <Separator className="mt-2"></Separator>
          </div>
          <PortCalls shipInfo={shipInfo}></PortCalls>
        </div>
      )}
    </div>
  );
};
